import { ProfessionalActivity } from "@models/clients/professionalActivity";
import { BankOpening } from "@models/clients/bankOpening";
import { Currency } from "@models/clients/currency";
import { ClientCRMRepo } from "@services/clients/clientCRM.repo";

class ClientManagementController implements angular.IOnInit {
  public isLoading = false;
  
  // Professional Activities data
  public professionalActivities: ProfessionalActivity[] = [];
  public professionalActivitiesColumns: any[] = [];
  public professionalActivitiesActions: any[] = [];
  public professionalActivitiesLoading = false;
  
  // Banks data
  public banks: BankOpening[] = [];
  public banksColumns: any[] = [];
  public banksActions: any[] = [];
  public banksLoading = false;

  // Currencies data
  public currencies: Currency[] = [];
  public currenciesColumns: any[] = [];
  public currenciesActions: any[] = [];
  public currenciesLoading = false;

  /** @ngInject */
  constructor(
    private clientCRMRepo: ClientCRMRepo,
    private $mdDialog: angular.material.IDialogService,
    private $rootScope: angular.IRootScopeService
  ) {}

  async $onInit() {
    await Promise.all([
      this.initializeProfessionalActivities(),
      this.initializeBanks(),
      this.initializeCurrencies()
    ]);
  }

  private async initializeProfessionalActivities() {
    // Define columns for professional activities table
    this.professionalActivitiesColumns = [
      { key: 'name', label: 'Activity Name', width: 'col-md-3' },
    ];

    // Load professional activities data
    try {
      this.professionalActivitiesLoading = true;
      this.professionalActivities = await this.clientCRMRepo.getProfessionalActivities().then(res => res.data as ProfessionalActivity[]);
    } finally {
      this.professionalActivitiesLoading = false;
    }
  }

  private async initializeBanks() {
    this.banksColumns = [
      { key: 'name', label: 'Bank Name', width: 'col-md-3' },
    ];

    // Load banks data
    try {
      this.banksLoading = true;
      this.banks = await this.clientCRMRepo.getBankOpenings().then(res => res.data as BankOpening[]);
    } finally {
      this.banksLoading = false;
    }
  }

  public refreshProfessionalActivities() {
    this.initializeProfessionalActivities();
  }
  
  public refreshBanks() {
    this.initializeBanks();
  }

  public refreshCurrencies() {
    this.initializeCurrencies();
  }

  // Professional Activities methods
  public addProfessionalActivity() {
    this.openProfessionalActivityModal();
  }

  private openProfessionalActivityModal(professionalActivity?: ProfessionalActivity) {
    const tempScope = this.$rootScope.$new(true) as any;
    tempScope.professionalActivity = professionalActivity || { id: 0, name: '' };

    this.$mdDialog
      .show({
        template: '<md-dialog flex="40" aria-label="Professional Activity Dialog"><professional-activity-modal professional-activity="professionalActivity"></professional-activity-modal></md-dialog>',
        clickOutsideToClose: true,
        scope: tempScope,
      })
      .then((result) => {
        if (result) {
          this.professionalActivitySaved(result);
        }
      });
  }

  private professionalActivitySaved(result: ProfessionalActivity) {
    if (result) {
      this.initializeProfessionalActivities();
    }
  }

  public editProfessionalActivity(item: ProfessionalActivity) {
    this.openProfessionalActivityModal(item);
  }

  public deleteProfessionalActivities(itemIds): angular.IPromise<void> {
    return this.clientCRMRepo.deleteProfessionalActivities(itemIds).then(() => {
      this.initializeProfessionalActivities();
    });
  }

  // Banks methods
  public addBank() {
    this.openBankModal();
  }

  private openBankModal(bank?: BankOpening) {
    const tempScope = this.$rootScope.$new(true) as any;
    tempScope.bank = bank || { id: 0, name: '' };

    this.$mdDialog
      .show({
        template: '<md-dialog flex="40" aria-label="Bank Dialog"><bank-modal bank="bank"></bank-modal></md-dialog>',
        clickOutsideToClose: true,
        scope: tempScope,
      })
      .then((result) => {
        if (result) {
          this.bankSaved(result);
        }
      });
  }

  private bankSaved(result: BankOpening) {
    if (result) {
      // Refresh the banks list to show the updated data
      this.initializeBanks();
    }
  }

  public editBank(item: BankOpening) {
    this.openBankModal(item);
  }

  public deleteBanks(itemIds): angular.IPromise<void> {
    return this.clientCRMRepo.deleteBankOpenings(itemIds).then(() => {
      this.initializeBanks();
    });
  }

  // Currencies methods
  private async initializeCurrencies() {
    this.currenciesColumns = [
      { key: 'name', label: 'Currency Name', width: 'col-md-3' },
      { key: 'code', label: 'Code', width: 'col-md-2' },
      { key: 'symbol', label: 'Symbol', width: 'col-md-2' },
      { key: 'exchangeRate', label: 'Exchange Rate', width: 'col-md-3' }
    ];

    // Load currencies data
    try {
      this.currenciesLoading = true;
      this.currencies = await this.clientCRMRepo.getCurrencies().then(res => res.data as Currency[]);
    } finally {
      this.currenciesLoading = false;
    }
  }

  public addCurrency() {
    this.openCurrencyModal();
  }

  private openCurrencyModal(currency?: Currency) {
    const tempScope = this.$rootScope.$new(true) as any;
    tempScope.currency = currency || { id: 0, name: '', symbol: '', code: '', exchangeRate: 0 };

    this.$mdDialog
      .show({
        template: '<md-dialog flex="40" aria-label="Currency Dialog"><currency-modal currency="currency"></currency-modal></md-dialog>',
        clickOutsideToClose: true,
        scope: tempScope,
      })
      .then((result) => {
        if (result) {
          this.currencySaved(result);
        }
      });
  }

  private currencySaved(result: Currency) {
    if (result) {
      // Refresh the currencies list to show the updated data
      this.initializeCurrencies();
    }
  }

  public editCurrency(item: Currency) {
    this.openCurrencyModal(item);
  }

  public deleteCurrencies(itemIds): angular.IPromise<void> {
    return this.clientCRMRepo.deleteCurrencies(itemIds).then(() => {
      this.initializeCurrencies();
    });
  }
}

export const clientManagement: angular.IComponentOptions = {
  template: require("./clientManagement.html"),
  controller: ClientManagementController,
};



// WEBPACK FOOTER //
// ./src/app/clientmanagement/clientManagement.ts